import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { api } from '../../lib/api'
import { ConnectError } from '../../gen/client'
import type { MeResponse } from '../../gen/types'

const MeContext = createContext<MeResponse | null>(null)

export function useCurrentUser(): MeResponse | null {
  return useContext(MeContext)
}

interface AuthGuardProps {
  children: ReactNode
}

export default function AuthGuard({ children }: AuthGuardProps) {
  const [me, setMe] = useState<MeResponse | null>(null)
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    api
      .me()
      .then((data) => {
        setMe(data)
        setChecked(true)
      })
      .catch((err: unknown) => {
        if (isUnauthenticated(err)) {
          window.location.href = '/login'
        } else {
          setChecked(true)
        }
      })
  }, [])

  if (!checked) {
    return (
      <div className="h-full flex items-center justify-center gap-1.5 bg-canvas">
        <Dot delay="0s" />
        <Dot delay="0.2s" />
        <Dot delay="0.4s" />
      </div>
    )
  }

  return <MeContext.Provider value={me}>{children}</MeContext.Provider>
}

function isUnauthenticated(err: unknown): boolean {
  if (err instanceof ConnectError && err.code === 'unauthenticated') return true
  const msg = (err instanceof Error ? err.message : String(err)).toLowerCase().replace(/_/g, ' ')
  return msg.includes('401') || msg.includes('unauthenticated') || msg.includes('not authenticated')
}

function Dot({ delay }: { delay: string }) {
  return (
    <span
      className="w-[6px] h-[6px] rounded-full bg-accent"
      style={{
        animation: 'typingBounce 1.2s ease-in-out infinite',
        animationDelay: delay,
      }}
    />
  )
}
